/**
 * Logger Utilities for Session Management
 * セッション管理の計測とモニタリング用ログ出力
 */

import { DEBUG_SESSION_LOGS } from './featureFlags';

/**
 * Log levels
 */
export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

/**
 * Session event types for tracking
 */
export const SessionEvent = {
  SESSION_START: 'session_start',
  SESSION_UPDATE: 'session_update', 
  SESSION_END: 'session_end',
  SESSION_PERSIST: 'session_persist',
  SESSION_ERROR: 'session_error',
  HEARTBEAT: 'heartbeat',
  NETWORK_ERROR: 'network_error',
  RETRY_ATTEMPT: 'retry_attempt'
};

/**
 * Enhanced logger with session tracking capabilities
 */
class SessionLogger {
  constructor() {
    this.metrics = {
      sessionStartCount: 0,
      sessionEndCount: 0,
      persistSuccessCount: 0,
      persistErrorCount: 0,
      heartbeatCount: 0,
      retryCount: 0,
      networkErrorCount: 0
    };
    
    this.sessionMetrics = new Map(); // videoId -> session metrics
  }

  /**
   * Log session event with metrics tracking
   * @param {string} event - Event type from SessionEvent
   * @param {Object} data - Event data
   * @param {string} level - Log level
   */
  logSessionEvent(event, data = {}, level = LogLevel.INFO) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      event,
      level,
      ...data
    };

    // Update metrics
    this._updateMetrics(event, data);

    // Console output based on level and debug settings
    if (DEBUG_SESSION_LOGS || level === LogLevel.ERROR) {
      const emoji = this._getEventEmoji(event);
      const message = `${emoji} [${event.toUpperCase()}] ${data.message || ''}`;
      
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(message, logEntry);
          break;
        case LogLevel.INFO:
          console.log(message, logEntry);
          break;
        case LogLevel.WARN:
          console.warn(message, logEntry);
          break;
        case LogLevel.ERROR:
          console.error(message, logEntry);
          break;
      }
    }

    // Send to analytics (future enhancement)
    this._sendToAnalytics(logEntry);

    return logEntry;
  }

  /**
   * Update internal metrics based on event
   */
  _updateMetrics(event, data) {
    switch (event) {
      case SessionEvent.SESSION_START:
        this.metrics.sessionStartCount++;
        if (data.videoId) {
          this._initSessionMetrics(data.videoId);
        }
        break;
      case SessionEvent.SESSION_END:
        this.metrics.sessionEndCount++;
        break;
      case SessionEvent.SESSION_PERSIST:
        if (data.success) {
          this.metrics.persistSuccessCount++;
        } else {
          this.metrics.persistErrorCount++;
        }
        break;
      case SessionEvent.HEARTBEAT:
        this.metrics.heartbeatCount++;
        break;
      case SessionEvent.RETRY_ATTEMPT:
        this.metrics.retryCount++;
        break;
      case SessionEvent.NETWORK_ERROR:
        this.metrics.networkErrorCount++;
        break;
    }
  }

  /**
   * Initialize session-specific metrics
   */
  _initSessionMetrics(videoId) {
    this.sessionMetrics.set(videoId, {
      startTime: Date.now(),
      updateCount: 0,
      persistAttempts: 0,
      persistFailures: 0,
      lastHeartbeat: Date.now()
    });
  }

  /**
   * Update session-specific metrics
   */
  updateSessionMetrics(videoId, updates) {
    const metrics = this.sessionMetrics.get(videoId);
    if (metrics) {
      Object.assign(metrics, updates);
    }
  }

  /**
   * Get emoji for event type
   */
  _getEventEmoji(event) {
    const emojiMap = {
      [SessionEvent.SESSION_START]: '🎬',
      [SessionEvent.SESSION_UPDATE]: '⏱️',
      [SessionEvent.SESSION_END]: '🏁',
      [SessionEvent.SESSION_PERSIST]: '💾',
      [SessionEvent.SESSION_ERROR]: '❌',
      [SessionEvent.HEARTBEAT]: '💓',
      [SessionEvent.NETWORK_ERROR]: '🌐',
      [SessionEvent.RETRY_ATTEMPT]: '🔄'
    };
    return emojiMap[event] || '📝';
  }

  /**
   * Send analytics data (placeholder for future implementation)
   */
  _sendToAnalytics(logEntry) {
    // Future: Send to analytics service
    // Could use Google Analytics, Mixpanel, or custom analytics
    if (process.env.NODE_ENV === 'development') {
      // In development, just store in localStorage for debugging
      try {
        const logs = JSON.parse(localStorage.getItem('session_analytics_logs') || '[]');
        logs.push(logEntry);
        // Keep only last 100 entries
        if (logs.length > 100) {
          logs.splice(0, logs.length - 100);
        }
        localStorage.setItem('session_analytics_logs', JSON.stringify(logs));
      } catch (error) {
        console.warn('Failed to store analytics log:', error);
      }
    }
  }

  /**
   * Get current metrics summary
   */
  getMetrics() {
    const now = Date.now();
    const activeSessionCount = this.sessionMetrics.size;
    const avgSessionDuration = this._calculateAvgSessionDuration();
    
    return {
      ...this.metrics,
      activeSessionCount,
      avgSessionDuration,
      successRate: this._calculateSuccessRate(),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Calculate success rate for persistence operations
   */
  _calculateSuccessRate() {
    const total = this.metrics.persistSuccessCount + this.metrics.persistErrorCount;
    if (total === 0) return 100;
    return Math.round((this.metrics.persistSuccessCount / total) * 100);
  }

  /**
   * Calculate average session duration
   */
  _calculateAvgSessionDuration() {
    if (this.sessionMetrics.size === 0) return 0;
    
    const now = Date.now();
    let totalDuration = 0;
    
    this.sessionMetrics.forEach(metrics => {
      totalDuration += now - metrics.startTime;
    });
    
    return Math.round(totalDuration / this.sessionMetrics.size / 1000); // seconds
  }

  /**
   * Get detailed session report
   */
  getSessionReport() {
    const metrics = this.getMetrics();
    const sessionDetails = Array.from(this.sessionMetrics.entries()).map(([videoId, data]) => ({
      videoId,
      duration: Math.round((Date.now() - data.startTime) / 1000),
      updateCount: data.updateCount,
      persistAttempts: data.persistAttempts,
      persistFailures: data.persistFailures,
      lastHeartbeat: new Date(data.lastHeartbeat).toISOString()
    }));

    return {
      summary: metrics,
      activeSessions: sessionDetails,
      generatedAt: new Date().toISOString()
    };
  }

  /**
   * Clear session metrics for ended session
   */
  clearSessionMetrics(videoId) {
    this.sessionMetrics.delete(videoId);
  }

  /**
   * Reset all metrics (for testing)
   */
  resetMetrics() {
    this.metrics = {
      sessionStartCount: 0,
      sessionEndCount: 0,
      persistSuccessCount: 0,
      persistErrorCount: 0,
      heartbeatCount: 0,
      retryCount: 0,
      networkErrorCount: 0
    };
    this.sessionMetrics.clear();
  }
}

// Export singleton logger instance
export const sessionLogger = new SessionLogger();

// Convenience methods for common logging patterns
export const logSessionStart = (videoId, sessionUuid, additionalData = {}) => {
  return sessionLogger.logSessionEvent(SessionEvent.SESSION_START, {
    videoId,
    sessionUuid,
    message: `Session started for video ${videoId}`,
    ...additionalData
  });
};

export const logSessionEnd = (videoId, sessionUuid, duration, additionalData = {}) => {
  return sessionLogger.logSessionEvent(SessionEvent.SESSION_END, {
    videoId,
    sessionUuid,
    duration,
    message: `Session ended for video ${videoId} (${duration}s)`,
    ...additionalData
  });
};

export const logSessionPersist = (videoId, sessionUuid, success, operation, additionalData = {}) => {
  return sessionLogger.logSessionEvent(SessionEvent.SESSION_PERSIST, {
    videoId,
    sessionUuid,
    success,
    operation,
    message: `Session persist ${success ? 'succeeded' : 'failed'} for video ${videoId} (${operation})`,
    ...additionalData
  }, success ? LogLevel.INFO : LogLevel.ERROR);
};

export const logHeartbeat = (videoId, totalSeconds, additionalData = {}) => {
  return sessionLogger.logSessionEvent(SessionEvent.HEARTBEAT, {
    videoId,
    totalSeconds,
    message: `Heartbeat for video ${videoId} (${totalSeconds}s)`,
    ...additionalData
  }, LogLevel.DEBUG);
};

export const logSessionError = (videoId, error, operation, additionalData = {}) => {
  return sessionLogger.logSessionEvent(SessionEvent.SESSION_ERROR, {
    videoId,
    error: error.message,
    operation,
    message: `Session error for video ${videoId} during ${operation}: ${error.message}`,
    ...additionalData
  }, LogLevel.ERROR);
};

export default sessionLogger;